import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCars } from "../../context/CarContext";
import emailjs from '@emailjs/browser';
import "./ReservationSection.css";
import { FiPhone } from "react-icons/fi";

const ReservationSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cars } = useCars();
  const form = useRef();
  
  // État du formulaire
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    voitureId: "",
    startDate: "",
    endDate: "",
    message: ""
  });
  
  // État pour les erreurs de validation
  const [errors, setErrors] = useState({});
  // État pour le message de succès
  const [success, setSuccess] = useState("");
  // État pour indiquer si le formulaire est en cours de soumission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupérer l'ID de la voiture depuis l'URL si disponible
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const voitureId = params.get("voitureId");
    if (voitureId) {
      setFormData(prev => ({ ...prev, voitureId }));
    }
  }, [location]);

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    
    // Mettre à jour l'URL si le modèle de voiture change
    if (id === "voitureId" && value) {
      // Mettre à jour l'URL avec le nouvel ID de voiture
      const params = new URLSearchParams(location.search);
      params.set("voitureId", value);
      navigate(`/reservation?${params.toString()}`, { replace: true });
      
      // Optionnel: Mettre à jour le titre de la page avec le nom de la voiture
      const selectedCar = cars.find(car => car.id === parseInt(value));
      if (selectedCar) {
        document.title = `Réservation ${selectedCar.name} - AMSEBRID`;
      }
    }
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: "" }));
    }
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Le nom est requis";
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Format d'email invalide";
    if (!formData.phone.trim()) newErrors.phone = "Le téléphone est requis";
    
    // Vérification de la voiture sélectionnée
    if (!formData.voitureId) {
      newErrors.voitureId = "Veuillez sélectionner une voiture";
    } else {
      // Vérifier si la voiture sélectionnée est disponible
      const selectedCar = cars.find(car => car.id === parseInt(formData.voitureId));
      if (selectedCar && !selectedCar.available) {
        newErrors.voitureId = "Cette voiture n'est plus disponible. Veuillez en choisir une autre.";
      }
    }
    
    if (!formData.startDate) newErrors.startDate = "La date de début est requise";
    if (!formData.endDate) newErrors.endDate = "La date de fin est requise";
    
    // Vérifier que la date de fin est après la date de début
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = "La date de fin doit être après la date de début";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      // Trouver la voiture sélectionnée pour inclure son nom dans l'email
      const selectedCar = cars.find(car => car.id === parseInt(formData.voitureId));
      const carName = selectedCar ? selectedCar.name : "Voiture non spécifiée";
      const carPrice = selectedCar ? `${selectedCar.price} ${selectedCar.currency || "DA"}/jour` : "";
      
      // Calculer la durée de la réservation
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const durationInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      // Configurer les paramètres pour EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        car_name: carName,
        car_price: carPrice,
        start_date: formData.startDate,
        end_date: formData.endDate,
        duration: durationInDays,
        message: formData.message,
        to_email: "autoadapte@gmail.com"
      };
      
      // IMPORTANT: Utiliser les mêmes identifiants EmailJS que le formulaire de contact
      const SERVICE_ID = "service_xmyy4qm"; // Service ID
      const TEMPLATE_ID = "template_ikfc0p5"; // Template ID pour les réservations
      const PUBLIC_KEY = "6kUkICTYBMQUXgTcL"; // Public Key
      
      emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      )
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setSuccess("Votre réservation a été envoyée avec succès! Nous vous contacterons bientôt pour confirmer les détails.");
        // Réinitialiser le formulaire
        setFormData({
          name: "",
          email: "",
          phone: "",
          voitureId: "",
          startDate: "",
          endDate: "",
          message: ""
        });
      })
      .catch((err) => {
        console.log('FAILED...', err);
        setErrors({
          form: "Une erreur est survenue lors de l'envoi de votre réservation. Veuillez réessayer plus tard ou nous contacter par téléphone."
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
    }
  };

  return (
    <section className="reservation-section">
      <div className="contact-info-container">
        <h2>Réservation rapide par WhatsApp</h2>
        <p className="contact-intro">
          Pour un service plus rapide et personnalisé, nous vous recommandons de nous contacter directement par WhatsApp.
          C'est le moyen le plus efficace pour obtenir des informations précises et réserver votre véhicule.
        </p>
        <div className="contact-info-items">
          <div className="contact-info-item">
            <FiPhone className="contact-info-icon" />
            <div>
              <h3>Whatsapp</h3>
              <p>
                <a 
                  href="https://wa.me/213797267104" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="phone-link"
                >
                  +213 797 267 104
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className="airport-delivery">
          <div className="airport-delivery-icon">✈️</div>
          <div className="airport-delivery-text">
            <strong>Service exclusif :</strong> Livraison gratuite de votre véhicule à l'aéroport de Bejaia
          </div>
        </div>
      </div>
      <div className="reservation-container">
        <h2>Ou réservez via notre formulaire</h2>
        <p className="reservation-intro">
          Si vous préférez, vous pouvez également remplir le formulaire ci-dessous pour réserver votre voiture.
          Nous vous contacterons dès que possible pour confirmer votre réservation.
        </p>
        
        {success && <div className="success-message">{success}</div>}
        {errors.form && <div className="error-message form-error">{errors.form}</div>}

        <form className="reservation-form" ref={form} onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Nom</label>
            <input 
              type="text" 
              id="name" 
              placeholder="Entrez votre nom" 
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? "error" : ""}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input 
              type="email" 
              id="email" 
              placeholder="Entrez votre email" 
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? "error" : ""}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Téléphone</label>
            <input 
              type="tel" 
              id="phone" 
              placeholder="Entrez votre numéro de téléphone" 
              value={formData.phone}
              onChange={handleChange}
              className={errors.phone ? "error" : ""}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="voitureId">Modèle de voiture</label>
            <div className="car-selection-container">
              <select 
                id="voitureId" 
                value={formData.voitureId}
                onChange={handleChange}
                className={errors.voitureId ? "error" : ""}
              >
                <option value="">Choisissez une voiture</option>
                {cars
                  .filter(car => car.available) // Filtrer uniquement les voitures disponibles
                  .map(car => (
                    <option key={car.id} value={car.id}>
                      {car.name} - {car.price} {car.currency || "DA"}/jour
                    </option>
                  ))
                }
              </select>
              {formData.voitureId && (
                <button 
                  type="button" 
                  className="view-details-button"
                  onClick={() => navigate(`/voitures/${formData.voitureId}`)}
                >
                  Voir détails
                </button>
              )}
              {cars.filter(car => car.available).length === 0 && (
                <div className="no-cars-available">
                  Aucune voiture disponible actuellement. Veuillez nous contacter par WhatsApp pour plus d'options.
                </div>
              )}
            </div>
            {errors.voitureId && <span className="error-message">{errors.voitureId}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Date de début</label>
              <input 
                type="date" 
                id="startDate" 
                value={formData.startDate}
                onChange={handleChange}
                className={errors.startDate ? "error" : ""}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.startDate && <span className="error-message">{errors.startDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">Date de fin</label>
              <input 
                type="date" 
                id="endDate" 
                value={formData.endDate}
                onChange={handleChange}
                className={errors.endDate ? "error" : ""}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
              />
              {errors.endDate && <span className="error-message">{errors.endDate}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message (optionnel)</label>
            <textarea 
              id="message" 
              placeholder="Informations supplémentaires" 
              value={formData.message}
              onChange={handleChange}
              rows="4"
            ></textarea>
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? "Envoi en cours..." : "Réserver"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ReservationSection;
